import { useMemo, useState } from "react";
import PropTypes from "prop-types";

// react-table
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
  useFilters,
} from "react-table";

// @mui
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Checkbox from "@mui/material/Checkbox";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDPagination from "components/MDPagination";

// Table cells
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

/* ---------------- Journey Type Filter UI ---------------- */
function JourneyTypeFilter({ column }) {
  const { setFilter } = column;
  const [anchorEl, setAnchorEl] = useState(null);
  const [checked, setChecked] = useState({
    PICKING: false,
    DROPPING: false,
  });

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (type) => {
    setChecked((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleOk = () => {
    const selected = Object.keys(checked).filter((k) => checked[k]);
    setFilter(selected.length ? selected : undefined);
    handleClose();
  };

  const handleReset = () => {
    setChecked({ PICKING: false, DROPPING: false });
    setFilter(undefined);
    handleClose();
  };

  return (
    <>
      <Icon
        fontSize="small"
        sx={{ cursor: "pointer", ml: 0.5 }}
        onClick={handleOpen}
      >
        filter_list
      </Icon>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "6px",
          },
        }}
      >
        <MDBox p={2} minWidth={180}>
          <Stack spacing={1}>
            <MDBox display="flex" alignItems="center">
              <Checkbox
                checked={checked.PICKING}
                onChange={() => handleChange("PICKING")}
              />
              <MDTypography variant="button">PICKING</MDTypography>
            </MDBox>

            <MDBox display="flex" alignItems="center">
              <Checkbox
                checked={checked.DROPPING}
                onChange={() => handleChange("DROPPING")}
              />
              <MDTypography variant="button">DROPPING</MDTypography>
            </MDBox>

            <Divider />

            <MDBox display="flex" justifyContent="space-between">
              <Button size="small" onClick={handleOk}>
                OK
              </Button>
              <Button size="small" onClick={handleReset}>
                Reset
              </Button>
            </MDBox>
          </Stack>
        </MDBox>
      </Popover>
    </>
  );
}

/* ---------------- Filter logic ---------------- */
function journeyTypeFilterFn(rows, id, filterValue) {
  return rows.filter((row) => filterValue.includes(row.values[id]));
}

/* ---------------- Main Table ---------------- */
function JourneyReportTable({
  table,
  entriesPerPage,
  showTotalEntries,
  isSorted,
  noEndBorder,
}) {
  const columns = useMemo(
    () =>
      table.columns.map((col) => {
        if (col.accessor === "journeyType") {
          return {
            ...col,
            Filter: JourneyTypeFilter,
            filter: "journeyTypeFilter",
          };
        }
        return col;
      }),
    [table.columns]
  );

  const data = useMemo(() => table.rows, [table.rows]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
  } = useTable(
    {
      columns,
      data,
      filterTypes: {
        journeyTypeFilter: journeyTypeFilterFn,
      },
    },
    useFilters,
    useSortBy,
    usePagination
  );

  return (
    <TableContainer
      sx={{
        maxHeight: "82vh",
        overflowY: "auto",
      }}
    >
      <Table {...getTableProps()} stickyHeader>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(
                    column.getSortByToggleProps ? column.getSortByToggleProps() : undefined
                  )}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#ffffff",
                    cursor: column.canSort ? "pointer" : "default",
                  }}
                >
                  <MDBox display="flex" alignItems="center">
                    {column.render("Header")}
                    {column.Filter && column.render("Filter")}
                  </MDBox>
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <DataTableBodyCell {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>

  );
}

JourneyReportTable.propTypes = {
  table: PropTypes.object.isRequired,
};

export default JourneyReportTable;
